import React, { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../utils/api-client';
import { safeParseJSON } from '../utils/api-utils';
import type { User } from '../types/user';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
}

interface AuthContextType extends AuthState {
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signInWithProvider: (provider: string, credential: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (params: { email: string; password: string; password_confirmation: string; name: string; surname: string; username: string; language: string }) => Promise<{ success: boolean; error?: string }>;
  /**
   * Takes on a session minted by an endpoint of its own — the guided
   * competition sign-up, which creates the parent, the children and the
   * entries in one call and hands back the same token body as /sign_up.
   * Stores it exactly as signUp does; the caller owns the message to show.
   */
  adoptSession: (data: { token: string; refresh_token: string; resource_owner: unknown }) => void;
  signOut: () => void;
  refreshToken: () => Promise<boolean>;
  updateUserData: (user: User) => void;
  /** Re-fetch the current user's profile (e.g. after earning a feature flag). */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'climb_log_auth_token';
const REFRESH_TOKEN_KEY = 'climb_log_refresh_token';
const USER_KEY = 'climb_log_user';

// Helper function to extract User from potentially wrapped data
const extractUserFromData = (data: unknown): User | null => {
  if (!data || typeof data !== 'object') return null;

  const obj = data as Record<string, unknown>;

  // If it's already a User object with expected properties, return it
  if (obj.id && obj.email) {
    return obj as unknown as User;
  }

  // If it's wrapped in a user property, extract it
  const userObj = obj.user as Record<string, unknown> | undefined;
  if (userObj && typeof userObj === 'object' && userObj.id && userObj.email) {
    return userObj as unknown as User;
  }

  return null;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true
  });
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const loadProfile = async (token: string) => {
    try {
      const response = await apiClient.get('/api/profile', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await safeParseJSON(response) as { data: { id: string; attributes: Record<string, unknown> } };
        const updatedUser: User = {
          ...data.data.attributes,
          id: parseInt(data.data.id)
        } as User;

        localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
        setState(prevState => ({ ...prevState, user: updatedUser }));
      }
    } catch (error) {
      console.error('Failed to load fresh profile data:', error);
    }
  };

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const userStr = localStorage.getItem(USER_KEY);

    if (token && userStr) {
      try {
        const storedData = JSON.parse(userStr);

        // Use helper to extract User object, handling potential wrapped format
        const user = extractUserFromData(storedData);

        if (!user) {
          throw new Error('Invalid user data format');
        }


        setState({
          isAuthenticated: true,
          user,
          token,
          loading: false
        });

        // Fetch fresh user data from profile API to ensure we have all fields
        loadProfile(token);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setState(prevState => ({ ...prevState, loading: false }));
      }
    } else {
      setState(prevState => ({ ...prevState, loading: false }));
    }
  }, []);

  // Everything a minted session leaves behind: the two tokens, the user, and
  // the profile fetch that fills in the fields the token body does not carry.
  const adoptSession = (data: { token: string; refresh_token: string; resource_owner: unknown }) => {
    const userData = extractUserFromData(data.resource_owner);

    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
    if (userData) localStorage.setItem(USER_KEY, JSON.stringify(userData));

    setState({
      isAuthenticated: true,
      user: userData,
      token: data.token,
      loading: false
    });

    loadProfile(data.token);
  };

  const signIn = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.post('/api/users/tokens/sign_in', { email, password });

      if (!response.ok) {
        const errorData = await safeParseJSON(response) as { error_description?: string | string[]; message?: string };
        const errorMessage = errorData.error_description
          ? Array.isArray(errorData.error_description)
            ? errorData.error_description.join(', ')
            : errorData.error_description
          : errorData.message || t('auth.failedToSignIn');

        toast({
          title: t('auth.signInError'),
          description: errorMessage,
          variant: 'destructive',
        });

        return { success: false, error: errorMessage };
      }

      const data = await safeParseJSON(response) as { token: string; refresh_token: string; resource_owner: { user?: User } | User };

      // Extract user data from resource_owner - handle both direct user object and wrapped { user: {...} } format
      const resourceOwner = data.resource_owner as { user?: User } | User;
      const userData = ('user' in resourceOwner && resourceOwner.user) ? resourceOwner.user : resourceOwner as User;

      // Save tokens and user data - ensure we save the clean User object
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      setState({
        isAuthenticated: true,
        user: userData,
        token: data.token,
        loading: false
      });

      loadProfile(data.token);

      toast({
        title: t('auth.signInSuccess'),
        description: t('auth.welcomeBack'),
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.unexpectedError');
      toast({
        title: t('auth.signInError'),
        description: errorMessage,
        variant: 'destructive',
      });
      return { success: false, error: errorMessage };
    }
  };

  // Sign in (or transparently sign up) via an external provider such as Google.
  // The frontend obtains a provider credential and the backend verifies it,
  // reusing an existing account when the email already exists. Kept generic so
  // additional providers only need a new value passed here.
  const signInWithProvider = async (provider: string, credential: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.post(`/api/users/auth/${provider}`, { credential });

      if (!response.ok) {
        const errorData = await safeParseJSON(response) as { error_description?: string | string[]; message?: string };
        const errorMessage = errorData.error_description
          ? Array.isArray(errorData.error_description)
            ? errorData.error_description.join(', ')
            : errorData.error_description
          : errorData.message || t('auth.failedToSignIn');

        toast({
          title: t('auth.signInError'),
          description: errorMessage,
          variant: 'destructive',
        });

        return { success: false, error: errorMessage };
      }

      const data = await safeParseJSON(response) as { token: string; refresh_token: string; resource_owner: { user?: User } | User };

      // Extract user data from resource_owner - handle both direct user object and wrapped { user: {...} } format
      const resourceOwner = data.resource_owner as { user?: User } | User;
      const userData = ('user' in resourceOwner && resourceOwner.user) ? resourceOwner.user : resourceOwner as User;

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      setState({
        isAuthenticated: true,
        user: userData,
        token: data.token,
        loading: false
      });

      loadProfile(data.token);

      toast({
        title: t('auth.signInSuccess'),
        description: t('auth.welcomeBack'),
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.unexpectedError');
      toast({
        title: t('auth.signInError'),
        description: errorMessage,
        variant: 'destructive',
      });
      return { success: false, error: errorMessage };
    }
  };

  const signUp = async (params: { email: string; password: string; password_confirmation: string; name: string; surname: string; username: string; language: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.post('/api/users/tokens/sign_up', params);

      if (!response.ok) {
        const errorData = await safeParseJSON(response) as { error_description?: string | string[]; message?: string };
        const errorMessage = errorData.error_description
          ? Array.isArray(errorData.error_description)
            ? errorData.error_description.join(', ')
            : errorData.error_description
          : errorData.message || t('auth.failedToSignUp');

        toast({
          title: t('auth.signUpError'),
          description: errorMessage,
          variant: 'destructive',
        });

        return { success: false, error: errorMessage };
      }

      const data = await safeParseJSON(response) as { token: string; refresh_token: string; resource_owner: { user?: User } | User };

      // Extract user data from resource_owner - handle both direct user object and wrapped { user: {...} } format
      const resourceOwner = data.resource_owner as { user?: User } | User;
      const userData = ('user' in resourceOwner && resourceOwner.user) ? resourceOwner.user : resourceOwner as User;

      // Save tokens and user data - ensure we save the clean User object
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      localStorage.setItem(USER_KEY, JSON.stringify(userData));

      setState({
        isAuthenticated: true,
        user: userData,
        token: data.token,
        loading: false
      });

      loadProfile(data.token);

      toast({
        title: t('auth.signUpSuccess'),
        description: t('auth.accountCreated'),
      });

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('auth.unexpectedError');
      toast({
        title: t('auth.signUpError'),
        description: errorMessage,
        variant: 'destructive',
      });
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);

      if (token) {
        // According to devise-api docs, revoke uses the access token as Bearer header
        await apiClient.post('/api/users/tokens/revoke', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      // Clear local storage regardless of API response
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);

      setState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false
      });

      toast({
        title: t('auth.signOutSuccess'),
        description: t('auth.signedOut'),
      });

      navigate('/');
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // According to devise-api docs, refresh uses the refresh token as Bearer header
      const response = await apiClient.post('/api/users/tokens/refresh', {}, {
        headers: {
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await safeParseJSON(response) as { token: string };

      // Save new token
      localStorage.setItem(TOKEN_KEY, data.token);

      setState(prevState => ({
        ...prevState,
        token: data.token,
      }));

      return true;
    } catch {
      // If refresh fails, sign out
      signOut();
      return false;
    }
  };

  const updateUserData = (user: User) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setState(prevState => ({
      ...prevState,
      user
    }));
  };

  const refreshProfile = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      await loadProfile(token);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signInWithProvider,
        signUp,
        adoptSession,
        signOut,
        refreshToken,
        updateUserData,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export type { AuthContextType };

